import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { categoriesApi, notesApi } from '../api';
import NoteCard from '../components/notes/NoteCard';
import NoteModal from '../components/notes/NoteModal';
import NoteDetailModal from '../components/notes/NoteDetailModal';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Icon } from '../components/common/Icons';
import { toast } from 'react-toastify';

const NOTES_PAGE_SIZE = 12;

export default function CategoriesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId: paramCategoryId } = useParams();

  // Categories list state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // All-Categories toolbar controls
  const [filterTab, setFilterTab] = useState(() => {
    try {
      const saved = localStorage.getItem('categories_filter_tab');
      return (saved === 'starred' || saved === 'all') ? saved : 'all';
    } catch {
      return 'all';
    }
  });

  const [categorySort, setCategorySort] = useState(() => {
    try {
      return localStorage.getItem('categories_sort_by') || 'recent'; // 'recent' | 'name' | 'name_desc' | 'notes'
    } catch {
      return 'recent';
    }
  });

  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('categories_view_mode') || 'grid'; // 'grid' | 'list'
    } catch {
      return 'grid';
    }
  });

  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Selected category (tracked via URL param / query)
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryNotes, setCategoryNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesPage, setNotesPage] = useState(0);
  const [notesTotalPages, setNotesTotalPages] = useState(0);
  const [notesTotalElements, setNotesTotalElements] = useState(0);

  // Category-specific notes toolbar controls (matches Home page bar)
  const [notesFilterTab, setNotesFilterTab] = useState('all'); // 'all' | 'starred'
  const [notesSortBy, setNotesSortBy] = useState('recent'); // 'recent' | 'hits' | 'title' | 'title_desc'
  const [notesViewMode, setNotesViewMode] = useState(() => {
    try {
      return localStorage.getItem('category_notes_view_mode') || 'grid'; // 'grid' | 'list'
    } catch {
      return 'grid';
    }
  });
  const [notesSearchQuery, setNotesSearchQuery] = useState('');

  // Active 3-dots dropdown tracking
  const [openMenuCategoryId, setOpenMenuCategoryId] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);

  // Modals & form state
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null);

  // Note modals
  const [editNote, setEditNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [deleteNote, setDeleteNote] = useState(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.card-hamburger-menu-wrap')) {
        setOpenMenuCategoryId(null);
      }
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Fetch categories list
  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await categoriesApi.list(categorySort);
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, [categorySort]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Determine active category ID from URL parameter or query string
  const activeCategoryId = useMemo(() => {
    if (paramCategoryId) return String(paramCategoryId);
    const qSelected = new URLSearchParams(location.search).get('selected');
    return qSelected ? String(qSelected) : null;
  }, [paramCategoryId, location.search]);

  // Keep selectedCategory synced with activeCategoryId in URL
  useEffect(() => {
    if (!activeCategoryId) {
      setSelectedCategory(null);
      return;
    }
    // Find category in loaded categories
    const found = categories.find(c => String(c.id) === activeCategoryId);
    if (found) {
      setSelectedCategory(found);
    } else if (categories.length > 0) {
      // If categories are already loaded and not found
      // could be newly created or fetch individually
      categoriesApi.list()
        .then(res => {
          const cat = res.data?.find(c => String(c.id) === activeCategoryId);
          if (cat) setSelectedCategory(cat);
        })
        .catch(() => {});
    }
  }, [activeCategoryId, categories]);

  // Load notes for selected category
  const loadCategoryNotes = useCallback(async (targetCatId, page = 0, sort = notesSortBy, starred = notesFilterTab === 'starred') => {
    if (!targetCatId) return;
    setNotesLoading(true);
    try {
      const { data } = await notesApi.list({
        categoryId: targetCatId,
        starred,
        favorite: starred,
        sort,
        page,
        size: NOTES_PAGE_SIZE,
      });

      if (data && data.content) {
        setCategoryNotes(data.content);
        setNotesTotalElements(data.totalElements || 0);
        setNotesTotalPages(data.totalPages || 0);
        setNotesPage(data.page || page);
      } else if (Array.isArray(data)) {
        setCategoryNotes(data);
        setNotesTotalElements(data.length);
        setNotesTotalPages(Math.ceil(data.length / NOTES_PAGE_SIZE));
      }
    } catch {
      setCategoryNotes([]);
      toast.error('Failed to load notes for this category');
    } finally {
      setNotesLoading(false);
    }
  }, [notesSortBy, notesFilterTab]);

  // Trigger loading notes when category or toolbar filters change
  useEffect(() => {
    if (activeCategoryId) {
      loadCategoryNotes(activeCategoryId, 0, notesSortBy, notesFilterTab === 'starred');
    }
  }, [activeCategoryId, notesSortBy, notesFilterTab, loadCategoryNotes]);

  // Listen for global category created/saved events from top header button
  useEffect(() => {
    const handleCategorySaved = () => {
      loadCategories();
    };
    window.addEventListener('memora:category-saved', handleCategorySaved);
    window.addEventListener('memora:folder-saved', handleCategorySaved);
    return () => {
      window.removeEventListener('memora:category-saved', handleCategorySaved);
      window.removeEventListener('memora:folder-saved', handleCategorySaved);
    };
  }, [loadCategories]);

  // Bug Fix: When a note is created/saved, keep user on current page and reload category notes!
  useEffect(() => {
    const handleNoteSaved = () => {
      if (activeCategoryId) {
        loadCategoryNotes(activeCategoryId, notesPage, notesSortBy, notesFilterTab === 'starred');
      }
      loadCategories();
    };
    window.addEventListener('memora:note-saved', handleNoteSaved);
    return () => {
      window.removeEventListener('memora:note-saved', handleNoteSaved);
    };
  }, [activeCategoryId, notesPage, notesSortBy, notesFilterTab, loadCategoryNotes, loadCategories]);

  // Persist toolbar preferences
  const handleToggleTab = (tab) => {
    setFilterTab(tab);
    try {
      localStorage.setItem('categories_filter_tab', tab);
    } catch {}
  };

  const handleChangeSort = (sort) => {
    setCategorySort(sort);
    try {
      localStorage.setItem('categories_sort_by', sort);
    } catch {}
  };

  const handleToggleViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('categories_view_mode', mode);
    } catch {}
  };

  const handleToggleNotesViewMode = (mode) => {
    setNotesViewMode(mode);
    try {
      localStorage.setItem('category_notes_view_mode', mode);
    } catch {}
  };

  // Toggle category star status
  const handleToggleCategoryStar = async (cat) => {
    if (!cat?.id) return;
    try {
      const { data } = await categoriesApi.toggleStar(cat.id);
      const isStarred = !!(data.starred ?? data.favorite);
      const updatedCategory = {
        ...cat,
        ...data,
        starred: isStarred,
        favorite: isStarred,
      };

      setCategories(prev => prev.map(c => String(c.id) === String(cat.id) ? updatedCategory : c));
      if (selectedCategory && String(selectedCategory.id) === String(cat.id)) {
        setSelectedCategory(updatedCategory);
      }
      toast.success(isStarred ? `Starred "${cat.name}"` : `Unstarred "${cat.name}"`);
      window.dispatchEvent(new CustomEvent('memora:category-saved', { detail: updatedCategory }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update category star');
    }
  };

  // Edit / Rename category
  const handleRenameCategory = async (e) => {
    e?.preventDefault();
    if (!editingCategory || !editCategoryName.trim()) return;
    try {
      const { data } = await categoriesApi.update(editingCategory.id, { name: editCategoryName.trim() });
      toast.success('Category renamed');
      setEditingCategory(null);
      if (selectedCategory?.id === editingCategory.id) {
        setSelectedCategory(data);
      }
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to rename category');
    }
  };

  // Delete category
  const handleDeleteCategory = async () => {
    if (!deleteCategoryTarget) return;
    try {
      await categoriesApi.delete(deleteCategoryTarget.id);
      toast.success(`Category "${deleteCategoryTarget.name}" deleted. Notes moved to default.`);
      if (selectedCategory?.id === deleteCategoryTarget.id) {
        navigate('/categories');
      }
      setDeleteCategoryTarget(null);
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete category');
    }
  };

  // Move note to trash
  const handleDeleteNote = async () => {
    if (!deleteNote) return;
    try {
      await notesApi.delete(deleteNote.id);
      toast.success(`"${deleteNote.title}" moved to Trash`);
      setDeleteNote(null);
      if (activeCategoryId) {
        loadCategoryNotes(activeCategoryId, notesPage, notesSortBy, notesFilterTab === 'starred');
      }
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete note');
    }
  };

  // Filtered & sorted categories for All Categories view
  const displayedCategories = useMemo(() => {
    let list = [...categories];

    if (filterTab === 'starred') {
      list = list.filter(c => !!(c.starred ?? c.favorite));
    }

    if (categorySearchQuery.trim()) {
      const q = categorySearchQuery.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q));
    }

    if (categorySort === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (categorySort === 'name_desc') {
      list.sort((a, b) => b.name.localeCompare(a.name));
    } else if (categorySort === 'notes') {
      list.sort((a, b) => (b.noteCount || 0) - (a.noteCount || 0));
    } else if (categorySort === 'recent') {
      list.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    }
    return list;
  }, [categories, filterTab, categorySearchQuery, categorySort]);

  // Filtered notes within the selected category
  const displayedCategoryNotes = useMemo(() => {
    if (!notesSearchQuery.trim()) return categoryNotes;
    const q = notesSearchQuery.toLowerCase();
    return categoryNotes.filter(n =>
      (n.title && n.title.toLowerCase().includes(q)) ||
      (n.content && n.content.toLowerCase().includes(q))
    );
  }, [categoryNotes, notesSearchQuery]);

  return (
    <div className="folders-page-container categories-page-container">
      {/* Top Header */}
      <div className="page-header">
        <div className="page-header-content" style={{ minWidth: 0, flex: 1 }}>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0 }}>
            <Icon name="category" size={24} style={{ flexShrink: 0 }} />
            <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>
              {selectedCategory ? selectedCategory.name : 'Categories'}
            </span>
          </h1>
        </div>

        {/* Top-right actions: 3-dots menu on opened category page */}
        <div className="page-header-actions" style={{ flexShrink: 0 }}>
          {selectedCategory && (
            <div className="card-hamburger-menu-wrap" ref={headerMenuRef}>
              <button
                type="button"
                className={`action-btn-pill menu-trigger ${headerMenuOpen ? 'active' : ''}`}
                title="Category options"
                onClick={e => {
                  e.stopPropagation();
                  setHeaderMenuOpen(prev => !prev);
                }}
                aria-label="Category options"
              >
                <Icon name="more-vertical" size={16} />
              </button>

              {headerMenuOpen && (
                <div className="card-menu-dropdown right-aligned category-menu-dropdown">
                  <button
                    type="button"
                    className="card-menu-item"
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      handleToggleCategoryStar(selectedCategory);
                    }}
                  >
                    <Icon name="star" size={15} filled={!!(selectedCategory.starred ?? selectedCategory.favorite)} />
                    <span>{(selectedCategory.starred ?? selectedCategory.favorite) ? 'Unstar' : 'Star'}</span>
                  </button>

                  {selectedCategory.name !== 'My Folder' && selectedCategory.name !== 'General' && (
                    <>
                      <button
                        type="button"
                        className="card-menu-item"
                        onClick={() => {
                          setHeaderMenuOpen(false);
                          setEditingCategory(selectedCategory);
                          setEditCategoryName(selectedCategory.name);
                        }}
                      >
                        <Icon name="edit" size={15} />
                        <span>Rename Category</span>
                      </button>
                      <button
                        type="button"
                        className="card-menu-item danger"
                        onClick={() => {
                          setHeaderMenuOpen(false);
                          setDeleteCategoryTarget(selectedCategory);
                        }}
                      >
                        <Icon name="trash" size={15} />
                        <span>Delete Category</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* VIEW: All Categories */}
      {!selectedCategory ? (
        <>
          {/* Top Bar matching Home page: Tabs (All / Starred), Search, Sort, View toggle */}
          <div className="library-toolbar">
            {/* Slider Toggle: All Categories / Starred */}
            <div className="toolbar-title-wrap">
              <div className="search-slider-toggle home-filter-slider" style={{ width: '270px' }}>
                <div className={`search-slider-pill ${filterTab === 'all' ? 'notes' : 'starred'}`} />
                <button
                  type="button"
                  className={`search-slider-btn ${filterTab === 'all' ? 'active' : ''}`}
                  onClick={() => handleToggleTab('all')}
                >
                  <Icon name="category" size={14} />
                  <span>All Categories</span>
                </button>
                <button
                  type="button"
                  className={`search-slider-btn ${filterTab === 'starred' ? 'active' : ''}`}
                  onClick={() => handleToggleTab('starred')}
                >
                  <Icon name="star" size={14} filled={filterTab === 'starred'} />
                  <span>Starred</span>
                </button>
              </div>
            </div>

            {/* Search Categories */}
            <div className="toolbar-search-wrap">
              <Icon name="search" size={14} className="toolbar-search-icon" />
              <input
                className="toolbar-search-input"
                placeholder="Search categories..."
                value={categorySearchQuery}
                onChange={e => setCategorySearchQuery(e.target.value)}
              />
              {categorySearchQuery && (
                <button
                  type="button"
                  className="toolbar-clear-btn"
                  onClick={() => setCategorySearchQuery('')}
                  aria-label="Clear category search"
                >
                  <Icon name="close" size={13} />
                </button>
              )}
            </div>

            <div className="toolbar-actions">
              {/* Sort Selector */}
              <div className="sort-dropdown-wrap">
                <Icon name="sort" size={14} className="sort-icon" />
                <select
                  className="sort-select"
                  value={categorySort}
                  onChange={e => handleChangeSort(e.target.value)}
                  aria-label="Sort categories"
                >
                  <option value="recent">Recently Active</option>
                  <option value="notes">Most Notes</option>
                  <option value="name">Alphabetically (A-Z)</option>
                  <option value="name_desc">Alphabetically (Z-A)</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="view-toggle-group">
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('grid')}
                  title="Grid View"
                  aria-label="Grid view"
                >
                  <Icon name="grid" size={16} />
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('list')}
                  title="List View"
                  aria-label="List view"
                >
                  <Icon name="list" size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Categories Grid or List */}
          {categoriesLoading ? (
            <div className="loading-state-wrap">
              <div className="spinner" />
              <p className="loading-text">Loading categories...</p>
            </div>
          ) : displayedCategories.length === 0 ? (
            <div className="empty-state-card">
              <div className="empty-state-icon-wrap">
                <Icon name={filterTab === 'starred' ? 'star' : 'category'} size={36} filled={filterTab === 'starred'} />
              </div>
              <h2 className="empty-state-title">
                {filterTab === 'starred'
                  ? 'No starred categories yet'
                  : categorySearchQuery
                  ? 'No categories match your search'
                  : 'No categories found'}
              </h2>
              <p className="empty-state-desc">
                {filterTab === 'starred'
                  ? 'You have not starred any category yet.'
                  : categorySearchQuery
                  ? 'Try a different search query.'
                  : 'Use the "+" button in the top navigation to create your first category.'}
              </p>
              {filterTab === 'starred' && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => handleToggleTab('all')}
                >
                  View All Categories
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            /* Rectangular Category Cards: 8 per row on desktop/laptop, 3 per row on mobile */
            <div className="categories-grid-rectangles">
              {displayedCategories.map(cat => {
                const isDefaultCat = cat.name === 'My Folder' || cat.name === 'General';
                const isMenuOpen = openMenuCategoryId === cat.id;

                return (
                  <div
                    key={cat.id}
                    className={`category-rect-card ${isMenuOpen ? 'menu-active' : ''}`}
                    onClick={() => navigate(`/categories/${cat.id}`)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="category-rect-top">
                      <div className="category-rect-top-left">
                        <span className="category-rect-mini-icon">
                          <Icon name="category" size={13} />
                        </span>
                        {isDefaultCat && (
                          <span className="category-default-badge">Default</span>
                        )}
                      </div>

                      <div className="category-rect-actions" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`action-btn-pill btn-star ${(cat.starred ?? cat.favorite) ? 'active' : ''}`}
                          title={(cat.starred ?? cat.favorite) ? 'Unstar Category' : 'Star Category'}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggleCategoryStar(cat);
                          }}
                          aria-label={(cat.starred ?? cat.favorite) ? 'Unstar category' : 'Star category'}
                        >
                          <Icon name="star" size={14} filled={!!(cat.starred ?? cat.favorite)} />
                        </button>

                        <div className="card-hamburger-menu-wrap">
                          <button
                            type="button"
                            className={`action-btn-pill menu-trigger ${isMenuOpen ? 'active' : ''}`}
                            title="More options"
                            onClick={e => {
                              e.stopPropagation();
                              setOpenMenuCategoryId(isMenuOpen ? null : cat.id);
                            }}
                            aria-label="Category options"
                          >
                            <Icon name="more-vertical" size={14} />
                          </button>

                          {isMenuOpen && (
                            <div
                              className="card-menu-dropdown right-aligned category-menu-dropdown"
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="card-menu-item"
                                onClick={e => {
                                  e.stopPropagation();
                                  setOpenMenuCategoryId(null);
                                  handleToggleCategoryStar(cat);
                                }}
                              >
                                <Icon name="star" size={14} filled={!!(cat.starred ?? cat.favorite)} />
                                <span>{(cat.starred ?? cat.favorite) ? 'Unstar' : 'Star'}</span>
                              </button>
                              {!isDefaultCat && (
                                <>
                                  <button
                                    type="button"
                                    className="card-menu-item"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setOpenMenuCategoryId(null);
                                      setEditingCategory(cat);
                                      setEditCategoryName(cat.name);
                                    }}
                                  >
                                    <Icon name="edit" size={14} />
                                    <span>Rename</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="card-menu-item danger"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setOpenMenuCategoryId(null);
                                      setDeleteCategoryTarget(cat);
                                    }}
                                  >
                                    <Icon name="trash" size={14} />
                                    <span>Delete</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="category-rect-body">
                      <h3 className="category-rect-name" title={cat.name}>
                        {cat.name}
                      </h3>
                      <span className="category-rect-pill">
                        {cat.noteCount} {cat.noteCount === 1 ? 'note' : 'notes'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Category List View */
            <div className="categories-list-view">
              {displayedCategories.map(cat => {
                const isDefaultCat = cat.name === 'My Folder' || cat.name === 'General';
                const isMenuOpen = openMenuCategoryId === cat.id;

                return (
                  <div
                    key={cat.id}
                    className={`category-list-row ${isMenuOpen ? 'menu-active' : ''}`}
                    onClick={() => navigate(`/categories/${cat.id}`)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="category-list-left">
                      <span className="category-list-icon">
                        <Icon name="category" size={18} />
                      </span>
                      <div className="category-list-details">
                        <div className="category-list-title-row">
                          <h3 className="category-list-title">{cat.name}</h3>
                          {isDefaultCat && <span className="category-default-badge">Default</span>}
                        </div>
                        <span className="category-list-count">
                          {cat.noteCount} {cat.noteCount === 1 ? 'note' : 'notes'}
                        </span>
                      </div>
                    </div>

                    <div className="category-list-actions" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`action-btn-pill btn-star ${(cat.starred ?? cat.favorite) ? 'active' : ''}`}
                        title={(cat.starred ?? cat.favorite) ? 'Unstar Category' : 'Star Category'}
                        onClick={e => {
                          e.stopPropagation();
                          handleToggleCategoryStar(cat);
                        }}
                        aria-label={(cat.starred ?? cat.favorite) ? 'Unstar category' : 'Star category'}
                      >
                        <Icon name="star" size={15} filled={!!(cat.starred ?? cat.favorite)} />
                      </button>

                      <div className="card-hamburger-menu-wrap">
                        <button
                          type="button"
                          className={`action-btn-pill menu-trigger ${isMenuOpen ? 'active' : ''}`}
                          title="More options"
                          onClick={e => {
                            e.stopPropagation();
                            setOpenMenuCategoryId(isMenuOpen ? null : cat.id);
                          }}
                          aria-label="Category options"
                        >
                          <Icon name="more-vertical" size={15} />
                        </button>

                        {isMenuOpen && (
                          <div
                            className="card-menu-dropdown right-aligned category-menu-dropdown"
                            onMouseDown={e => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="card-menu-item"
                              onClick={e => {
                                e.stopPropagation();
                                setOpenMenuCategoryId(null);
                                handleToggleCategoryStar(cat);
                              }}
                            >
                              <Icon name="star" size={14} filled={!!(cat.starred ?? cat.favorite)} />
                              <span>{(cat.starred ?? cat.favorite) ? 'Unstar' : 'Star'}</span>
                            </button>
                            {!isDefaultCat && (
                              <>
                                <button
                                  type="button"
                                  className="card-menu-item"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setOpenMenuCategoryId(null);
                                    setEditingCategory(cat);
                                    setEditCategoryName(cat.name);
                                  }}
                                >
                                  <Icon name="edit" size={14} />
                                  <span>Rename Category</span>
                                </button>
                                <button
                                  type="button"
                                  className="card-menu-item danger"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setOpenMenuCategoryId(null);
                                    setDeleteCategoryTarget(cat);
                                  }}
                                >
                                  <Icon name="trash" size={14} />
                                  <span>Delete Category</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        /* VIEW: Inside Specific Category Page (With Spacious Boundary Padding) */
        <div className="category-detail-view">
          {/* Top Bar for Category Notes (Same bar as on Home page: All Notes / Starred, Search, Sort, Grid/List view) */}
          <div className="library-toolbar">
            {/* Slider Toggle: All Notes / Starred */}
            <div className="toolbar-title-wrap">
              <div className="search-slider-toggle home-filter-slider" style={{ width: '250px' }}>
                <div className={`search-slider-pill ${notesFilterTab === 'all' ? 'notes' : 'starred'}`} />
                <button
                  type="button"
                  className={`search-slider-btn ${notesFilterTab === 'all' ? 'active' : ''}`}
                  onClick={() => setNotesFilterTab('all')}
                >
                  <Icon name="note" size={14} />
                  <span>All Notes</span>
                </button>
                <button
                  type="button"
                  className={`search-slider-btn ${notesFilterTab === 'starred' ? 'active' : ''}`}
                  onClick={() => setNotesFilterTab('starred')}
                >
                  <Icon name="star" size={14} filled={notesFilterTab === 'starred'} />
                  <span>Starred</span>
                </button>
              </div>
            </div>

            {/* Search notes within this category */}
            <div className="toolbar-search-wrap">
              <Icon name="search" size={14} className="toolbar-search-icon" />
              <input
                className="toolbar-search-input"
                placeholder={`Search in ${selectedCategory.name}...`}
                value={notesSearchQuery}
                onChange={e => setNotesSearchQuery(e.target.value)}
              />
              {notesSearchQuery && (
                <button
                  type="button"
                  className="toolbar-clear-btn"
                  onClick={() => setNotesSearchQuery('')}
                  aria-label="Clear notes search"
                >
                  <Icon name="close" size={13} />
                </button>
              )}
            </div>

            <div className="toolbar-actions">
              {/* Sort Selector */}
              <div className="sort-dropdown-wrap">
                <Icon name="sort" size={14} className="sort-icon" />
                <select
                  className="sort-select"
                  value={notesSortBy}
                  onChange={e => setNotesSortBy(e.target.value)}
                  aria-label="Sort category notes"
                >
                  <option value="recent">Recently Active</option>
                  <option value="hits">Most Viewed</option>
                  <option value="title">Alphabetically (A-Z)</option>
                  <option value="title_desc">Alphabetically (Z-A)</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="view-toggle-group">
                <button
                  type="button"
                  className={`view-toggle-btn ${notesViewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => handleToggleNotesViewMode('grid')}
                  title="Grid View"
                  aria-label="Grid view"
                >
                  <Icon name="grid" size={16} />
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${notesViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => handleToggleNotesViewMode('list')}
                  title="List View"
                  aria-label="List view"
                >
                  <Icon name="list" size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Notes Content */}
          {notesLoading ? (
            <div className="loading-state-wrap">
              <div className="spinner" />
              <p className="loading-text">Loading notes in {selectedCategory.name}...</p>
            </div>
          ) : displayedCategoryNotes.length === 0 ? (
            <div className="empty-state-card">
              <div className="empty-state-icon-wrap">
                <Icon name={notesFilterTab === 'starred' ? 'star' : 'note'} size={36} filled={notesFilterTab === 'starred'} />
              </div>
              <h2 className="empty-state-title">
                {notesFilterTab === 'starred'
                  ? `No starred notes in "${selectedCategory.name}" yet`
                  : notesSearchQuery
                  ? 'No notes match your search'
                  : `No notes in "${selectedCategory.name}" yet`}
              </h2>
              <p className="empty-state-desc">
                {notesFilterTab === 'starred'
                  ? 'Open any note and click the star icon in the top right to mark it as starred.'
                  : notesSearchQuery
                  ? 'Try a different search keyword.'
                  : 'Click the "+" icon in the top header to create a note in this category.'}
              </p>
              {notesFilterTab === 'starred' && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setNotesFilterTab('all')}
                >
                  View All Notes in {selectedCategory.name}
                </button>
              )}
            </div>
          ) : (
            <>
              {notesViewMode === 'grid' ? (
                <div className="notes-responsive-grid docs-responsive-grid">
                  {displayedCategoryNotes.map(note => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      layout="grid"
                      onClick={() => setViewNote(note)}
                      onEdit={n => setEditNote(n)}
                      onDelete={n => setDeleteNote(n)}
                    />
                  ))}
                </div>
              ) : (
                <div className="notes-list-stack docs-list-stack">
                  {displayedCategoryNotes.map(note => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      layout="list"
                      onClick={() => setViewNote(note)}
                      onEdit={n => setEditNote(n)}
                      onDelete={n => setDeleteNote(n)}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              <Pagination
                page={notesPage}
                totalPages={notesTotalPages}
                totalElements={notesTotalElements}
                size={NOTES_PAGE_SIZE}
                onPageChange={p => loadCategoryNotes(activeCategoryId, p, notesSortBy, notesFilterTab === 'starred')}
                loading={notesLoading}
              />
            </>
          )}
        </div>
      )}

      {/* Modal: Rename Category */}
      {editingCategory && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setEditingCategory(null); }}>
          <div className="modal-sheet-box modal-sm" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2 className="modal-title">Rename Category</h2>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setEditingCategory(null)}
                aria-label="Close"
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <form onSubmit={handleRenameCategory}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-category-name">
                    Category Name
                  </label>
                  <input
                    id="edit-category-name"
                    className="form-input"
                    value={editCategoryName}
                    onChange={e => setEditCategoryName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setEditingCategory(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary-custom"
                  disabled={!editCategoryName.trim()}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Note Inspection Modal */}
      {viewNote && (
        <NoteDetailModal
          note={viewNote}
          onClose={() => setViewNote(null)}
          onEdit={noteToEdit => {
            setViewNote(null);
            setEditNote(noteToEdit);
          }}
          onDelete={() => {
            setViewNote(null);
            setDeleteNote(viewNote);
          }}
          onNoteUpdated={updated => {
            setCategoryNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
          }}
        />
      )}

      {/* Note Edit Modal */}
      {editNote && (
        <NoteModal
          note={editNote}
          initialCategoryId={selectedCategory?.id}
          onClose={() => setEditNote(null)}
          onSaved={updated => {
            setCategoryNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
            loadCategoryNotes(activeCategoryId, notesPage, notesSortBy, notesFilterTab === 'starred');
            loadCategories();
          }}
        />
      )}

      {/* Confirm Delete Category Modal */}
      {deleteCategoryTarget && (
        <ConfirmDialog
          title={`Delete Category "${deleteCategoryTarget.name}"?`}
          message="Deleting this category will safely preserve all its notes by moving them to your default category."
          confirmLabel="Delete Category"
          onConfirm={handleDeleteCategory}
          onCancel={() => setDeleteCategoryTarget(null)}
          danger
        />
      )}

      {/* Confirm Delete Note Modal */}
      {deleteNote && (
        <ConfirmDialog
          title={`Move "${deleteNote.title}" to Trash?`}
          message="This note will be safely moved to Trash for 7 days before permanent removal."
          confirmLabel="Move to Trash"
          onConfirm={handleDeleteNote}
          onCancel={() => setDeleteNote(null)}
          danger
        />
      )}
    </div>
  );
}
